import * as React from "react";
import { Checkbox, FormControlLabel } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import useAuthFetch from "../../hooks/useAuthFetch";
import ConfirmBox from "../ConfirmBox";
import VerificationSummary from "./VerificationSummary";
import toast from "react-hot-toast";

// Create / edit page for a named face verification settings profile (see the
// verification capping plan for the config shape). Profiles are reusable and
// linked to agents on the Agent-Template Associations page.

// hiddenApis: checks that can't run on a page and are always saved as off.
// Personal Details is where the reference face is captured, so there is
// nothing to match against yet.
const PAGES = [
  { key: "PersonalDetails", label: "Personal Details", hiddenApis: ["faceMatch"] },
  { key: "PolicyDetails", label: "Policy Details" },
  { key: "VideoConsent", label: "Video Consent" },
  { key: "Qna", label: "Q&A" },
];

// Order matches the order the checks run in on each page.
const APIS = [
  { key: "faceDetect", label: "Face Detect" },
  { key: "spoof", label: "Spoof" },
  { key: "faceMatch", label: "Face Match" },
];

const isHiddenApi = (page, api) =>
  !!PAGES.find((p) => p.key === page)?.hiddenApis?.includes(api);

// Same ranges the backend validates against.
const LIMITS = {
  callsPerAttempt: { min: 1, max: 20 },
  maxAttempts: { min: 1, max: 5 },
  gapSec: { min: 1, max: 60 },
  initialDelaySec: { min: 0, max: 10 },
  cooldownHours: { min: 0, max: 72 },
  cooldownMinutes: { min: 0, max: 59 },
  cooldownSeconds: { min: 0, max: 59 },
};

// Wait before a customer can retry after "Video Verification Failed", stored
// as retryCooldownSec; edited as hours / minutes / seconds.
const MAX_COOLDOWN_SEC = 72 * 3600;

const splitCooldown = (sec) => {
  const total = Number.isInteger(sec) && sec > 0 ? sec : 0;
  return {
    cooldownHours: String(Math.floor(total / 3600)),
    cooldownMinutes: String(Math.floor((total % 3600) / 60)),
    cooldownSeconds: String(total % 60),
  };
};

const joinCooldown = (form) =>
  Number(form.cooldownHours || 0) * 3600 +
  Number(form.cooldownMinutes || 0) * 60 +
  Number(form.cooldownSeconds || 0);

// Used to prefill inputs when a disabled API has no saved values, so enabling
// it gives the admin something sensible to start from.
const FALLBACK_API = {
  callsPerAttempt: 3,
  maxAttempts: 2,
  gapMs: 5000,
  successGapMs: 10000,
  failGapMs: 5000,
};

const msToSec = (ms) =>
  ms === undefined || ms === null || ms === "" ? "" : String(Number(ms) / 1000);
const secToMs = (sec) => Math.round(Number(sec) * 1000);
const valOr = (v, fallback) => (v === undefined || v === null ? fallback : v);

// API config (ms) -> form values (seconds, strings for inputs)
const configToForm = (config) => {
  const pages = {};
  PAGES.forEach(({ key: page }) => {
    const pageCfg = config?.pages?.[page] || {};
    const pageForm = { initialDelaySec: msToSec(pageCfg.initialDelayMs) };
    APIS.forEach(({ key: api }) => {
      const apiCfg = pageCfg[api] || {};
      pageForm[api] = {
        enabled: !!apiCfg.enabled && !isHiddenApi(page, api),
        mode: apiCfg.mode || null,
        callsPerAttempt: String(valOr(apiCfg.callsPerAttempt, FALLBACK_API.callsPerAttempt)),
        maxAttempts: String(valOr(apiCfg.maxAttempts, FALLBACK_API.maxAttempts)),
        gapSec: msToSec(valOr(apiCfg.gapMs, FALLBACK_API.gapMs)),
        successGapSec: msToSec(valOr(apiCfg.successGapMs, FALLBACK_API.successGapMs)),
        failGapSec: msToSec(valOr(apiCfg.failGapMs, FALLBACK_API.failGapMs)),
      };
    });
    pages[page] = pageForm;
  });
  return { pages, ...splitCooldown(config?.retryCooldownSec) };
};

const isIntInRange = (value, { min, max }) => {
  if (value === "" || value === null || value === undefined) return false;
  const n = Number(value);
  return Number.isInteger(n) && n >= min && n <= max;
};

const isNumInRange = (value, { min, max }) => {
  if (value === "" || value === null || value === undefined) return false;
  const n = Number(value);
  return Number.isFinite(n) && n >= min && n <= max;
};

const fieldKey = (page, api, field) =>
  api ? `${page}.${api}.${field}` : `${page}.${field}`;

const validateForm = (form) => {
  const errors = {};
  const gapMsg = `Must be between ${LIMITS.gapSec.min} and ${LIMITS.gapSec.max} seconds`;

  PAGES.forEach(({ key: page }) => {
    const pageForm = form.pages[page];
    if (!isNumInRange(pageForm.initialDelaySec, LIMITS.initialDelaySec)) {
      errors[fieldKey(page, null, "initialDelaySec")] =
        `Must be between ${LIMITS.initialDelaySec.min} and ${LIMITS.initialDelaySec.max} seconds`;
    }
    APIS.forEach(({ key: api }) => {
      const a = pageForm[api];
      if (!a.enabled) return;
      if (a.mode === "continuous") {
        if (!isNumInRange(a.successGapSec, LIMITS.gapSec)) {
          errors[fieldKey(page, api, "successGapSec")] = gapMsg;
        }
        if (!isNumInRange(a.failGapSec, LIMITS.gapSec)) {
          errors[fieldKey(page, api, "failGapSec")] = gapMsg;
        }
      } else {
        if (!isIntInRange(a.callsPerAttempt, LIMITS.callsPerAttempt)) {
          errors[fieldKey(page, api, "callsPerAttempt")] =
            `Must be a whole number between ${LIMITS.callsPerAttempt.min} and ${LIMITS.callsPerAttempt.max}`;
        }
        if (!isIntInRange(a.maxAttempts, LIMITS.maxAttempts)) {
          errors[fieldKey(page, api, "maxAttempts")] =
            `Must be a whole number between ${LIMITS.maxAttempts.min} and ${LIMITS.maxAttempts.max}`;
        }
        if (!isNumInRange(a.gapSec, LIMITS.gapSec)) {
          errors[fieldKey(page, api, "gapSec")] = gapMsg;
        }
      }
    });
  });

  const cooldownFields = ["cooldownHours", "cooldownMinutes", "cooldownSeconds"];
  const badCooldown = cooldownFields.find(
    (f) => !isIntInRange(form[f], LIMITS[f])
  );
  if (badCooldown) {
    errors.retryCooldown =
      "Use whole numbers: hours 0–72, minutes and seconds 0–59";
  } else if (joinCooldown(form) > MAX_COOLDOWN_SEC) {
    errors.retryCooldown = "The wait can be at most 72 hours";
  }

  return errors;
};

// Form values -> API config (ms). Disabled APIs only carry values that are
// valid, so a half-typed disabled row never fails backend validation (the
// backend merges anything missing from its defaults).
const formToConfig = (form) => {
  const pages = {};
  PAGES.forEach(({ key: page }) => {
    const pageForm = form.pages[page];
    const pageCfg = { initialDelayMs: secToMs(pageForm.initialDelaySec) };
    APIS.forEach(({ key: api }) => {
      const a = pageForm[api];
      const apiCfg = { enabled: a.enabled && !isHiddenApi(page, api) };
      if (a.mode === "continuous") {
        apiCfg.mode = "continuous";
        if (a.enabled || isNumInRange(a.successGapSec, LIMITS.gapSec)) {
          apiCfg.successGapMs = secToMs(a.successGapSec);
        }
        if (a.enabled || isNumInRange(a.failGapSec, LIMITS.gapSec)) {
          apiCfg.failGapMs = secToMs(a.failGapSec);
        }
      } else {
        if (a.enabled || isIntInRange(a.callsPerAttempt, LIMITS.callsPerAttempt)) {
          apiCfg.callsPerAttempt = Number(a.callsPerAttempt);
        }
        if (a.enabled || isIntInRange(a.maxAttempts, LIMITS.maxAttempts)) {
          apiCfg.maxAttempts = Number(a.maxAttempts);
        }
        if (a.enabled || isNumInRange(a.gapSec, LIMITS.gapSec)) {
          apiCfg.gapMs = secToMs(a.gapSec);
        }
      }
      pageCfg[api] = apiCfg;
    });
    pages[page] = pageCfg;
  });
  return { pages, retryCooldownSec: joinCooldown(form) };
};

const NumberField = ({ label, value, onChange, error, step }) => (
  <div className="flex flex-col mr-6 mt-2">
    <div className="flex items-center">
      <label className="block text-black-150 text-sm">{label}</label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        type="number"
        step={step}
        className="ml-3 p-2 w-24 text-black border border-gray-450 rounded-md"
      />
    </div>
    {error && <p className="text-red-400 text-sm my-1">{error}</p>}
  </div>
);

const NAME_MAX_LENGTH = 100;
const LIST_PATH = "/dashboard/verification-settings";

const validateName = (name) => {
  const trimmed = name.trim();
  if (!trimmed) return "Name is required";
  if (trimmed.length > NAME_MAX_LENGTH) {
    return `Name must be at most ${NAME_MAX_LENGTH} characters`;
  }
  return null;
};

export default function VerificationProfileForm() {
  const customFetch = useAuthFetch();
  const navigate = useNavigate();
  const { profileId } = useParams();
  const isCreate = !profileId;

  const [name, setName] = React.useState("");
  const [isDefault, setIsDefault] = React.useState(false);
  const [openResetConfirm, setOpenResetConfirm] = React.useState(false);
  const [form, setForm] = React.useState(null);
  const [defaults, setDefaults] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState(null);
  const [isSaving, setIsSaving] = React.useState(false);
  const [errors, setErrors] = React.useState({});
  const [open, setOpen] = React.useState(false);
  const [errMsg, setErrMsg] = React.useState(null);

  // Create mode starts from the backend defaults (returned by the list
  // endpoint); edit mode loads the saved profile.
  const fetchConfig = async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const url = isCreate
        ? new URL(
            `${process.env.REACT_APP_API_URL}/v1/dashboard/verificationProfile/list`
          )
        : new URL(
            `${process.env.REACT_APP_API_URL}/v1/dashboard/verificationProfile/get`
          );
      if (!isCreate) url.searchParams.set("profileId", profileId);
      const { response, status, message } = await customFetch(url, "GET");
      const responseDefaults = response?.defaults || null;

      if (status !== 200 || !response) {
        setLoadError(
          message || "Something went wrong while fetching verification settings!"
        );
      } else if (isCreate) {
        if (responseDefaults) {
          setName("");
          setForm(configToForm(responseDefaults));
          setDefaults(responseDefaults);
        } else {
          setLoadError("Default verification settings are unavailable");
        }
      } else if (response.profile) {
        setName(response.profile.name || "");
        setIsDefault(!!response.profile.isDefault);
        setForm(configToForm(response.profile.config || responseDefaults));
        setDefaults(responseDefaults);
      } else {
        setLoadError("Verification settings profile not found");
      }
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : String(error));
    }
    setIsLoading(false);
  };

  React.useEffect(() => {
    setErrors({});
    fetchConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId]);

  const clearError = (key) => {
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const setCooldownField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    clearError("retryCooldown");
  };

  const setPageField = (page, field, value) => {
    setForm((prev) => ({
      ...prev,
      pages: {
        ...prev.pages,
        [page]: { ...prev.pages[page], [field]: value },
      },
    }));
    clearError(fieldKey(page, null, field));
  };

  const setApiField = (page, api, field, value) => {
    setForm((prev) => ({
      ...prev,
      pages: {
        ...prev.pages,
        [page]: {
          ...prev.pages[page],
          [api]: { ...prev.pages[page][api], [field]: value },
        },
      },
    }));
    clearError(fieldKey(page, api, field));
  };

  // Resets the settings only; the profile name is kept.
  const handleReset = () => {
    if (!defaults) return;
    setForm(configToForm(defaults));
    setErrors((prev) => (prev.name ? { name: prev.name } : {}));
  };

  const handleSave = async () => {
    const validationErrors = validateForm(form);
    const nameError = validateName(name);
    if (nameError) validationErrors.name = nameError;
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      toast.error("Please fix the highlighted fields", {
        position: "bottom-right",
      });
      return;
    }

    setIsSaving(true);
    try {
      const url = new URL(
        `${process.env.REACT_APP_API_URL}/v1/dashboard/verificationProfile/${
          isCreate ? "create" : "update"
        }`
      );
      const bodyContent = {
        name: name.trim(),
        config: formToConfig(form),
      };
      if (!isCreate) bodyContent.profileId = profileId;

      const { response, status, message } = await customFetch(
        url,
        "POST",
        bodyContent
      );

      if (status !== (isCreate ? 201 : 200)) {
        const errorMessage =
          message ||
          response?.message ||
          "Something went wrong while saving verification settings!";
        setErrMsg(errorMessage);
        setOpen(true);
        toast.error(errorMessage, {
          position: "bottom-right",
          duration: 5000,
        });
        console.log("Error saving verification profile", response);
      } else {
        toast.success(
          isCreate
            ? "Verification settings profile created!"
            : "Verification settings profile updated!",
          { position: "bottom-right" }
        );
        setIsSaving(false);
        navigate(LIST_PATH);
        return;
      }
    } catch (error) {
      console.error("Error saving verification profile:", error);
      toast.error("Something went wrong while saving verification settings!", {
        position: "bottom-right",
      });
    }
    setIsSaving(false);
  };

  return (
    <div className="flex flex-col">
      <div className="flex flex-row mb-4 items-center px-4 py-1">
        <button
          onClick={() => navigate(LIST_PATH)}
          className="mr-2 flex items-center text-black-150"
          title="Back to Face Verification Settings"
        >
          <ArrowBack fontSize="small" />
        </button>
        <p className="text-xl mb-0 font-extrabold">
          {isCreate
            ? "Create Face Verification Settings"
            : "Edit Face Verification Settings"}
        </p>
      </div>
      <div
        style={{
          height: `calc(100vh - 190px)`,
          overflowY: "auto",
        }}
        className="mx-3 scrollbar-primary"
      >
        {isLoading ? (
          <div className="w-full py-3 flex flex-col gap-3">
            <div className="h-14 w-full bg-gray-300 rounded relative overflow-hidden animate-pulse" />
            <div className="h-14 w-full bg-gray-300 rounded relative overflow-hidden animate-pulse" />
            <div className="h-14 w-full bg-gray-300 rounded relative overflow-hidden animate-pulse" />
          </div>
        ) : loadError || !form ? (
          <div className="py-3 flex items-center">
            <p className="text-red-400 text-sm my-1">
              {loadError || "Verification settings are unavailable"}
            </p>
            <button
              onClick={fetchConfig}
              className="ml-3 px-2 py-1 rounded-md bg-slate-300"
            >
              Retry
            </button>
          </div>
        ) : (
          <div className="flex gap-4 items-start">
            <div className="flex-1 min-w-0">
            <div className="flex flex-col">
              <div className="flex items-center">
                <label className="block text-black-150">Name</label>
                <input
                  type="text"
                  value={name}
                  maxLength={NAME_MAX_LENGTH}
                  onChange={(e) => {
                    setName(e.target.value);
                    clearError("name");
                  }}
                  placeholder="e.g. Strict"
                  className="ml-4 px-3 py-2 border border-gray-450 rounded-md w-1/3 text-black-150"
                />
              </div>
              {errors.name && (
                <p className="text-red-400 text-sm my-1">{errors.name}</p>
              )}
              {isDefault && (
                <p className="mt-3 px-3 py-2 rounded-md bg-blue-50 text-blue-800 text-sm">
                  These are the default settings. They apply to every
                  Agent-Template Association with no verification settings
                  linked.
                </p>
              )}
            </div>

            <div className="mt-6 flex flex-col">
              <p className="text-black-150 mb-0">
                Wait before the customer can retry after &quot;Video
                Verification Failed&quot;
              </p>
              <div className="flex flex-wrap items-center">
                <NumberField
                  label="Hours"
                  value={form.cooldownHours}
                  onChange={(v) => setCooldownField("cooldownHours", v)}
                />
                <NumberField
                  label="Minutes"
                  value={form.cooldownMinutes}
                  onChange={(v) => setCooldownField("cooldownMinutes", v)}
                />
                <NumberField
                  label="Seconds"
                  value={form.cooldownSeconds}
                  onChange={(v) => setCooldownField("cooldownSeconds", v)}
                />
              </div>
              <p className="text-gray-500 text-xs mt-1 mb-0">
                0 hours 0 minutes 0 seconds = the customer can retry
                immediately. Maximum 72 hours.
              </p>
              {errors.retryCooldown && (
                <p className="text-red-400 text-sm my-1">{errors.retryCooldown}</p>
              )}
            </div>

            <div className="mt-6 border-2 border-gray-300 flex flex-col">
              <p className="text-base border-b-2 px-3 py-2.5 bg-gray-100 border-gray-300 font-bold text-black-150 mb-0">
                Face Verification Settings
              </p>
              <div className="px-3 py-3">
                {PAGES.map(({ key: page, label: pageLabel }) => {
                  const pageForm = form.pages[page];
                  return (
                    <div
                      key={page}
                      className="mb-4 pb-3 border-b border-gray-300 last:border-b-0"
                    >
                      <p className="text-black-150 font-semibold mb-1">{pageLabel}</p>
                      <NumberField
                        label="Initial delay (sec)"
                        value={pageForm.initialDelaySec}
                        step="0.5"
                        onChange={(v) => setPageField(page, "initialDelaySec", v)}
                        error={errors[fieldKey(page, null, "initialDelaySec")]}
                      />
                      {APIS.filter(({ key: api }) => !isHiddenApi(page, api)).map(({ key: api, label: apiLabel }) => {
                        const a = pageForm[api];
                        const continuous = a.mode === "continuous";
                        return (
                          <div key={api} className="mt-2 ml-2 flex flex-wrap items-start">
                            <div className="w-40 flex items-center">
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={a.enabled}
                                    onChange={(e) =>
                                      setApiField(page, api, "enabled", e.target.checked)
                                    }
                                  />
                                }
                                label={apiLabel}
                              />
                            </div>
                            {a.enabled && continuous && (
                              <>
                                <NumberField
                                  label="Gap after success (sec)"
                                  value={a.successGapSec}
                                  onChange={(v) => setApiField(page, api, "successGapSec", v)}
                                  error={errors[fieldKey(page, api, "successGapSec")]}
                                />
                                <NumberField
                                  label="Gap after failure (sec)"
                                  value={a.failGapSec}
                                  onChange={(v) => setApiField(page, api, "failGapSec", v)}
                                  error={errors[fieldKey(page, api, "failGapSec")]}
                                />
                              </>
                            )}
                            {a.enabled && !continuous && (
                              <>
                                <NumberField
                                  label="Calls per attempt"
                                  value={a.callsPerAttempt}
                                  onChange={(v) => setApiField(page, api, "callsPerAttempt", v)}
                                  error={errors[fieldKey(page, api, "callsPerAttempt")]}
                                />
                                <NumberField
                                  label="Max attempts"
                                  value={a.maxAttempts}
                                  onChange={(v) => setApiField(page, api, "maxAttempts", v)}
                                  error={errors[fieldKey(page, api, "maxAttempts")]}
                                />
                                <NumberField
                                  label="Gap (sec)"
                                  value={a.gapSec}
                                  onChange={(v) => setApiField(page, api, "gapSec", v)}
                                  error={errors[fieldKey(page, api, "gapSec")]}
                                />
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
            </div>
            {/* Plain-English reading of the settings above, kept in view while scrolling. */}
            <aside className="w-96 shrink-0 sticky top-0 self-start">
              <VerificationSummary form={form} pages={PAGES} apis={APIS} />
            </aside>
          </div>
        )}
      </div>
      <div className="mt-4 flex justify-end px-4">
        <button
          onClick={() => navigate(LIST_PATH)}
          disabled={isSaving}
          className="bg-[#F3F4F8] text-black-150 font-semibold px-3.5 py-2 rounded disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={() => setOpenResetConfirm(true)}
          disabled={!defaults || !form || isSaving}
          className="ml-3 bg-[#F3F4F8] text-black-150 font-semibold px-3.5 py-2 rounded disabled:opacity-50"
        >
          Reset to defaults
        </button>
        <button
          onClick={handleSave}
          disabled={!form || isLoading || isSaving}
          className="ml-3 bg-purple-350 text-white font-semibold px-3.5 py-2 rounded disabled:opacity-50"
        >
          {isSaving ? "Saving..." : "Save"}
        </button>
      </div>
      <ConfirmBox
        open={open}
        setOpen={setOpen}
        buttonText={"Close"}
        text={errMsg ? errMsg : "An error occurred"}
      />
      <ConfirmBox
        open={openResetConfirm}
        setOpen={setOpenResetConfirm}
        buttonText={"Reset"}
        buttonHandler={() => {
          setOpenResetConfirm(false);
          handleReset();
        }}
        extraButtonText={"Cancel"}
        extraButtonHandler={() => setOpenResetConfirm(false)}
        text="Reset all settings to the system defaults? Your unsaved changes will be lost. Nothing is saved until you click Save."
      />
    </div>
  );
}
